import { canMoveSubtreeUnder, FormElementKind, FormElementTableColumnKind, logicAncestry, revealedSubtreeIds, type FormElement } from '@gearbelt/global';
import { match } from 'ts-pattern';

import type { FormDesignerForm, FormElementConfig } from './form-designer.types';

// Whether a card (the subtree rooted at `sourceId`) may drop into the reveal box of `triggerId`. Rejected when
// the drop would cycle — `sourceId` is the box's trigger or an ancestor of it — or push the moved subtree past
// the nesting cap. The one guard the DnD monitor consults at hit-test and commit time; exported so the real
// predicate (not a stub) is exercised against real element trees in tests.
export function canDropCardIntoRuleBox(elements: FormElement[], sourceId: string, triggerId: string): boolean {
  return !logicAncestry(elements, triggerId).includes(sourceId) && canMoveSubtreeUnder(elements, sourceId, triggerId);
}

// The catalogue is passed in rather than imported: importing it from
// form-designer.tsx at runtime would create a cycle.

// Throws because callers render from the result. Anything putting an element into
// designer state must go through loadableElements, or this fires.
export function definitionFor<T extends { kind: FormElementKind }>(kind: FormElementKind, definitions: readonly T[]): T {
  const definition = definitions.find((candidate) => candidate.kind === kind);
  if (!definition) {
    throw new Error(`No form designer definition for element kind "${kind}".`);
  }
  return definition;
}

export function loadableElements(elements: FormElement[], definitions: readonly { kind: FormElementKind }[]): FormElement[] {
  return elements.filter((element) => definitions.some((definition) => definition.kind === element.kind));
}

// The drag outcomes as pure functions — what a drop does, decoupled from the DnD library, so they're
// unit-testable without simulating a pointer drag (which JSDOM cannot drive).

type NewElementDefinition = { kind: FormElementKind; label: string; description?: string; configuratorDefault?: FormElementConfig<FormElementKind> };

// A toolbox item's drag identity is its label, not a UUID; returns null when the label matches no entry.
export function buildDroppedElement(label: string, definitions: readonly NewElementDefinition[]): FormElement | null {
  const definition = definitions.find((candidate) => candidate.label === label);
  if (!definition) {
    return null;
  }
  return {
    id: crypto.randomUUID(),
    config: getDefaultFormDesignerElementConfig(definition.kind, definition.configuratorDefault),
    label: definition.label,
    description: definition.description,
    required: false,
    kind: definition.kind,
  } as FormElement;
}

// The selection index in a rebuilt elements array: where the previously-selected element's id now sits.
// Tracking by id survives any mutation (reorder, reveal in/out) that shuffles both flat-canvas and
// full-array positions; returns null when nothing was selected or the element is gone.
export function selectedIndexById(elements: FormElement[], selectedId: string | null): number | null {
  if (selectedId === null) {
    return null;
  }
  const index = elements.findIndex((element) => element.id === selectedId);
  return index === -1 ? null : index;
}

// Remove the element at `index` and cascade-delete its entire revealed subtree: a deleted trigger takes its
// revealed fields with it, and any nested triggers among those take their own descendants too. Leaving a
// revealed field behind would orphan its `dependsOn` back-pointer to a field that no longer exists — invisible
// on the canvas yet still persisted (and rejected by the dependsOn schema on save). revealedSubtreeIds owns
// the cycle-safe walk and the single definition of "revealed child" shared with the depth utils.
export function elementsAfterDelete(elements: FormElement[], index: number): FormElement[] {
  const deleted = elements[index];
  if (!deleted) {
    return elements.filter((_, i) => i !== index);
  }
  const removedIds = revealedSubtreeIds(elements, deleted);
  removedIds.add(deleted.id);
  return elements.filter((element) => !removedIds.has(element.id));
}

// A clone of `sourceElement` with a fresh id and no logicRules: a copied rule would share the source's rule
// id, so both triggers would render the same revealed fields and a drag-out would act on the wrong box.
export function clonedElement(sourceElement: FormElement, newId: string): FormElement {
  const { logicRules: _removedRules, ...rest } = sourceElement;
  return { ...rest, id: newId };
}

// Deep-clones the subtree revealed by one rule, for the "clone rule" action. Returns a fresh rule id and the
// cloned elements (NOT inserted — the caller splices them in). Every element, rule id, and back-pointer in the
// subtree is regenerated so the clone is fully independent: a revealed field keeps its own nested rules + their
// children, re-wired to the new ids. Top-level dependents of the source rule are re-pointed at `newRuleId`.
export function cloneRuleSubtree(elements: FormElement[], triggerId: string, sourceRuleId: string): { newRuleId: string; clonedElements: FormElement[] } {
  const newRuleId = crypto.randomUUID();
  const clonedElements: FormElement[] = [];
  // Each source element is cloned at most once — guards against a malformed cyclic subtree recursing forever
  // (the UI prevents cycles, but corrupt persisted data could carry one), matching the cycle-safe walks in
  // logic-depth.util.ts.
  const cloned = new Set<string>();

  // Clone every element revealed by `parentRuleId` (on `parentId`), recursing into any that carry their own
  // rules. `newParentId`/`newParentRuleId` are the already-cloned ids the cloned child must point back at.
  const cloneDependents = (parentId: string, parentRuleId: string, newParentId: string, newParentRuleId: string) => {
    for (const element of elements) {
      if (element.dependsOn?.fieldId !== parentId || element.dependsOn.logicRuleId !== parentRuleId) {
        continue;
      }
      if (cloned.has(element.id)) {
        continue;
      }
      cloned.add(element.id);
      const newId = crypto.randomUUID();
      // Re-point this clone at its cloned parent + the cloned rule it now belongs to; give it fresh rule ids.
      const ruleIdRemap = new Map((element.logicRules ?? []).map((rule) => [rule.id, crypto.randomUUID()] as const));
      const remappedRuleId = (ruleId: string): string => {
        const remapped = ruleIdRemap.get(ruleId);
        if (remapped === undefined) {
          throw new Error(`cloneRuleSubtree: rule ${ruleId} missing from remap for element ${element.id}`);
        }
        return remapped;
      };
      const clone: FormElement = {
        ...element,
        id: newId,
        dependsOn: { fieldId: newParentId, logicRuleId: newParentRuleId },
        ...(element.logicRules ? { logicRules: element.logicRules.map((rule) => ({ ...rule, id: remappedRuleId(rule.id) })) } : {}),
      };
      clonedElements.push(clone);
      // Recurse: this element's own rules reveal their own children, which must be cloned under the fresh ids.
      for (const rule of element.logicRules ?? []) {
        cloneDependents(element.id, rule.id, newId, remappedRuleId(rule.id));
      }
    }
  };

  cloneDependents(triggerId, sourceRuleId, triggerId, newRuleId);
  return { newRuleId, clonedElements };
}

// The flat-array index at which to splice an element so it occupies box slot `boxSlot` of `ruleId`. The box
// renders the rule's revealed members in flat-array order, so a box slot maps to the flat index of the
// current member at that slot; appending goes just after the last member, or just after the trigger when the
// box is empty. Pass `elements` with the moved element already removed so slot math isn't off by one.
export function boxSlotToFlatInsertIndex(elements: FormElement[], ruleId: string, boxSlot: number, triggerId: string): number {
  const memberFlatIndices = elements
    .map((el, index) => ({ el, index }))
    .filter(({ el }) => el.dependsOn?.logicRuleId === ruleId)
    .map(({ index }) => index);
  if (boxSlot < memberFlatIndices.length) {
    return memberFlatIndices[boxSlot];
  }
  if (memberFlatIndices.length > 0) {
    return memberFlatIndices[memberFlatIndices.length - 1] + 1;
  }
  const triggerIndex = elements.findIndex((el) => el.id === triggerId);
  return triggerIndex === -1 ? elements.length : triggerIndex + 1;
}

// Full-array `from`/`to` indices for a reorder WITHIN a reveal box, from member-relative slots. `toIndex` can
// be the append slot (`members.length`, "move to the end of the box") when the drop lands on the box gutter
// rather than a member card — clamp it to the last member's slot so the move still runs instead of being
// dropped. Returns null when the rule has no members or an index doesn't resolve.
export function revealReorderFlatIndices(
  elements: FormElement[],
  ruleId: string,
  fromIndex: number,
  toIndex: number,
): { fromFlat: number; toFlat: number } | null {
  const memberFlatIndices = elements
    .map((el, index) => ({ el, index }))
    .filter(({ el }) => el.dependsOn?.logicRuleId === ruleId)
    .map(({ index }) => index);
  if (memberFlatIndices.length === 0) return null;
  const fromFlat = memberFlatIndices[fromIndex];
  const toFlat = memberFlatIndices[Math.min(toIndex, memberFlatIndices.length - 1)];
  if (fromFlat === undefined || toFlat === undefined) return null;
  return { fromFlat, toFlat };
}

// The flat-array index for a flat-canvas slot. The canvas excludes revealed fields, so slot N is the array
// position of the Nth non-revealed element; appending goes at the end. Pass `elements` with the moved element
// already removed and `revealedIds` reflecting that removal.
export function flatCanvasIndexToArrayIndex(elements: FormElement[], revealedIds: Set<string>, canvasIndex: number): number {
  let seen = 0;
  for (let i = 0; i < elements.length; i++) {
    if (revealedIds.has(elements[i].id)) continue;
    if (seen === canvasIndex) return i;
    seen++;
  }
  return elements.length;
}

// Reorder so each trigger is immediately followed by the fields its rules reveal (recursively), keeping the
// non-revealed elements in their existing order. The fill-time renderer groups a revealed field under a
// trigger purely by array position, so this is what keeps a form's layout correct after any sequence of
// drags. Pure; a cycle or an orphaned dependent (trigger missing) can't loop — emitted-tracking guards both.
export function orderElementsByTrigger(elements: FormElement[]): FormElement[] {
  const revealedByTrigger = new Map<string, FormElement[]>();
  for (const element of elements) {
    const triggerId = element.dependsOn?.fieldId;
    if (triggerId) {
      const group = revealedByTrigger.get(triggerId) ?? [];
      group.push(element);
      revealedByTrigger.set(triggerId, group);
    }
  }

  const emitted = new Set<string>();
  const ordered: FormElement[] = [];
  const emit = (element: FormElement) => {
    if (emitted.has(element.id)) return; // guards cycles and double-emits
    emitted.add(element.id);
    ordered.push(element);
    for (const child of revealedByTrigger.get(element.id) ?? []) emit(child);
  };

  // Walk in existing order; a revealed field is emitted by its trigger, so skip it at the top level. An
  // orphan (its trigger isn't in the array) is never reached by a trigger, so emit it where it sits.
  const byId = new Set(elements.map((element) => element.id));
  for (const element of elements) {
    const triggerId = element.dependsOn?.fieldId;
    if (triggerId && byId.has(triggerId)) continue;
    emit(element);
  }
  return ordered;
}

export function validateForm(type: number, elements: FormDesignerForm['elements']): { isValid: boolean; error: string | null } {
  // General validations (apply to all form types)
  const unitReadingElements = elements.filter((el) => el.kind === FormElementKind.UnitReading);

  if (unitReadingElements.length > 0) {
    if (!unitReadingElements.every((el) => el.config?.unitId)) {
      return { isValid: false, error: 'Cannot save form because a Unit Reading field has no Unit selected.' };
    }
  }

  const tableElements = elements.filter((el) => el.kind === FormElementKind.Table);
  if (tableElements.some((el) => el.config.columns.length === 0)) {
    return { isValid: false, error: 'Cannot save form because a table has no columns.' };
  }

  // A conditional-logic rule needs both a match value and at least one revealed field, or it can't save
  // (LogicRuleSchema requires values.min(1)) and would never reveal anything.
  const rulesWithoutValues = elements.some((el) => el.logicRules?.some((rule) => rule.values.length === 0));
  if (rulesWithoutValues) {
    return { isValid: false, error: 'Cannot save form because a conditional-logic rule has no match value selected.' };
  }
  const ruleHasNoRevealedField = elements.some((el) => el.logicRules?.some((rule) => !elements.some((other) => other.dependsOn?.logicRuleId === rule.id)));
  if (ruleHasNoRevealedField) {
    return { isValid: false, error: 'Cannot save form because a conditional-logic rule has no fields to show.' };
  }

  return { isValid: true, error: null };
}

export function getDefaultFormDesignerElementConfig(
  kind: FormElementKind,
  configuratorDefault?: FormElementConfig<FormElementKind>,
): FormElementConfig<FormElementKind> {
  return match(kind)
    .with(
      FormElementKind.PassFailCheck,
      () =>
        ({
          options: {
            pass: { disabled: false },
            fail: { disabled: false },
          },
          issue: { label: 'Issue', disabled: false, requirePhotos: false },
        }) satisfies FormElementConfig<FormElementKind.PassFailCheck>,
    )
    .with(
      FormElementKind.PassFailWithNaCheck,
      () =>
        ({
          options: {
            pass: { disabled: false },
            fail: { disabled: false },
            na: { disabled: false },
          },
          issue: { label: 'Issue', disabled: false, requirePhotos: false },
        }) satisfies FormElementConfig<FormElementKind.PassFailWithNaCheck>,
    )
    .with(
      FormElementKind.Date,
      () =>
        (configuratorDefault as FormElementConfig<FormElementKind.Date>) ??
        ({
          showDate: true,
          showTime: true,
          isSubmissionDate: false,
        } satisfies FormElementConfig<FormElementKind.Date>),
    )
    .with(
      FormElementKind.AssetSelect,
      () =>
        ({
          multiple: false,
        }) satisfies FormElementConfig<FormElementKind.AssetSelect>,
    )
    .with(
      FormElementKind.IssueSelect,
      () =>
        ({
          requirePhotos: true,
        }) satisfies FormElementConfig<FormElementKind.IssueSelect>,
    )
    .with(
      FormElementKind.Dropdown,
      () =>
        ({
          source: 'static',
          options: [],
          multiple: true,
        }) satisfies FormElementConfig<FormElementKind.Dropdown>,
    )
    .with(
      FormElementKind.Table,
      () =>
        ({
          columns: [
            {
              id: crypto.randomUUID(),
              kind: FormElementTableColumnKind.SingleLineInput,
              label: 'New Column',
              config: undefined,
            },
          ],
        }) satisfies FormElementConfig<FormElementKind.Table>,
    )
    .with(
      FormElementKind.SignatureArray,
      () =>
        ((configuratorDefault as FormElementConfig<FormElementKind.SignatureArray>) ?? {
          isManualSignature: true,
        }) satisfies FormElementConfig<FormElementKind.SignatureArray>,
    )
    .with(
      FormElementKind.LocationSelect,
      () =>
        ({
          populateLastLocation: true,
        }) satisfies FormElementConfig<FormElementKind.LocationSelect>,
    )
    .with(
      FormElementKind.SingleLineInput,
      FormElementKind.MultiLineInput,
      FormElementKind.Heading,
      FormElementKind.ReadingHours,
      FormElementKind.Photo,
      FormElementKind.PdfAttachment,
      FormElementKind.ReadingMileage,
      FormElementKind.PreFilledText,
      // Below are elements that are not present in the template builder
      // They must remain as they are used in Table columns
      FormElementKind.Space,
      FormElementKind.Number,
      () => undefined,
    )
    .with(
      FormElementKind.UnitReading,
      () =>
        // The designer holds a partial config until a unit is picked; validateForm blocks saving a
        // Unit Reading field with no unitId, so the cast can only be observed mid-configuration.
        ({ allowLowerReading: false }) as FormElementConfig<FormElementKind.UnitReading>,
    )
    .exhaustive();
}
